import { useState, useEffect } from "react";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Clock, AlertTriangle } from "lucide-react";

import { useAuth } from "@/hooks/useAuth";

export function DailyTrialPopup() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [daysLeft, setDaysLeft] = useState(0);

  useEffect(() => {
    // Only run if we have a resolved user session to prevent guest false-positives
    if (!user) return;

    const checkTrial = () => {
      const isTrial = localStorage.getItem("billing_is_trial") === "true";
      const days = parseInt(localStorage.getItem("billing_trial_days") || "0", 10);
      
      if (!isTrial) return;

      setDaysLeft(days);

      const todayStr = format(new Date(), 'yyyy-MM-dd');
      const storageKey = `billing_trial_popup_last_shown_${user.id}`;
      const lastShown = localStorage.getItem(storageKey);

      if (lastShown !== todayStr) {
        // Delay opening slightly so it doesn't jarringly block the initial load
        setTimeout(() => {
          setOpen(true);
          localStorage.setItem(storageKey, todayStr);
        }, 1500);
      }
    };

    checkTrial();
  }, [user]);

  const handlePayNow = () => {
    // Redirect to Laravel subscription page where they can pay
    window.location.href = "https://setusuvidha.com/subscription";
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md bg-gradient-to-br from-white to-amber-50 dark:from-slate-900 dark:to-slate-800 border-amber-200 dark:border-amber-900">
        <DialogHeader>
          <div className="mx-auto bg-amber-100 dark:bg-amber-900/30 p-3 rounded-full mb-2">
            <Clock className="w-8 h-8 text-amber-600 dark:text-amber-400" />
          </div>
          <DialogTitle className="text-center text-xl font-bold text-slate-800 dark:text-white">
            15-Day Free Trial
          </DialogTitle>
          <DialogDescription className="text-center text-slate-600 dark:text-slate-300">
            You are currently using the free trial of the Billing Module. 
          </DialogDescription>
        </DialogHeader>

        <div className="my-4 py-4 px-4 bg-white dark:bg-slate-800 rounded-xl border border-amber-100 dark:border-amber-800 text-center shadow-sm">
          <p className="text-sm font-semibold mb-1 uppercase tracking-wider text-slate-500 dark:text-slate-400">Days Remaining</p>
          <div className="text-4xl font-black text-amber-600 dark:text-amber-400">
            {daysLeft}
          </div>
          <p className="text-xs text-slate-500 mt-2 flex items-center justify-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            Please make the payment before the trial ends to avoid interruption.
          </p>
        </div>

        <DialogFooter className="flex flex-col sm:flex-row gap-2 mt-2">
          <Button variant="outline" className="w-full sm:w-auto" onClick={() => setOpen(false)}>
            Close
          </Button>
          <Button className="w-full sm:w-auto bg-amber-600 hover:bg-amber-700 text-white font-bold" onClick={handlePayNow}>
            Pay ₹499 Now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
